import { forwardRef, useImperativeHandle, useRef } from 'react';
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  Toolbar,
} from 'react-simple-wysiwyg';

interface TemplateEditorProps {
  value?: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  allowRawHtmlPaste?: boolean;
}

export interface TemplateEditorRef {
  insertText: (text: string) => void;
}

export const TemplateEditor = forwardRef<
  TemplateEditorRef,
  TemplateEditorProps
>(({ value = '', onChange, disabled = false }, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (editorRef.current) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.setStartAfter(range.endContainer);
          range.setEndAfter(range.endContainer);
          selection.removeAllRanges();
          selection.addRange(range);

          // Trigger change event
          const event = new Event('input', { bubbles: true });
          editorRef.current.dispatchEvent(event);
        } else {
          // If no selection, append to the end
          if (editorRef.current) {
            editorRef.current.innerHTML += text;
            // Trigger change event
            const event = new Event('input', { bubbles: true });
            editorRef.current.dispatchEvent(event);
          }
        }
      }
    },
  }));

  const handleChange: React.ComponentProps<typeof Editor>['onChange'] = e => {
    onChange(e.target.value);
  };

  return (
    <div className="template-editor">
      <Editor
        ref={editorRef}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder="Saisissez votre contenu..."
        containerProps={{
          style: {
            minHeight: '200px',
            border: '1px solid #d1d5db',
            borderRadius: '0.75rem',
          },
        }}
      >
        <Toolbar>
          <BtnBold />
          <BtnItalic />
          <BtnLink />
          <BtnBulletList />
          <BtnNumberedList />
        </Toolbar>
      </Editor>
    </div>
  );
});

TemplateEditor.displayName = 'TemplateEditor';
